import React, { useMemo, useState } from "react"
import { useSortBy, useTable } from "react-table"
import { Label, Table } from "reactstrap"
import { Stack } from "@mui/system"
import { DROPSHIP_PARTNERS } from "utils/constants"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
} from "reactstrap"
import PuffLoading from "components/Loading/PuffLoading"
import { useMutation } from "@apollo/client"
import { EXPORT_FILE } from "graphql/mutations/order"
import CancelIcon from "@mui/icons-material/Cancel"

function removeTypename(obj) {
  if (Array.isArray(obj)) {
    return obj.map(removeTypename)
  } else if (obj && typeof obj === "object") {
    const newObj = {}
    for (const key in obj) {
      if (key !== "__typename") {
        newObj[key] = removeTypename(obj[key])
      }
    }
    return newObj
  }
  return obj
}

const DropshipTable = ({
  onCancelClick,
  selectedDropshipProductId,
  setIsDropshipAddedProduct,
  orderData,
  clearSelectedDropshipProductId,
  dropshipData,
  setDropshipData,
}) => {
  const [loading, setLoading] = useState(false)
  const [exportFile] = useMutation(EXPORT_FILE)
  const columnData = useMemo(
    () => [
      {
        Header: "ORDER.LINE.ID",
        accessor: "line_id",
        Cell: ({ value }) => {
          return <span>{value}</span>
        },
      },
      {
        Header: "SKU",
        accessor: "sku",
        Cell: ({ value }) => {
          return <span>{value}</span>
        },
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value }) => {
          return <span>{value}</span>
        },
      },
      {
        Header: "Destination",
        accessor: "destination",
        Cell: ({ value, row }) => {
          const handleChange = e => {
            const updatedData = [...dropshipData]
            updatedData[row.index] = {
              ...updatedData[row.index],
              destination: e.target.value,
            }
            setDropshipData(updatedData)
          }

          return (
            <select
              className="form-select"
              value={value}
              onChange={handleChange}
            >
              <option value="customer">Customer</option>
              <option value="customer_franco">Customer (Franco)</option>
            </select>
          )
        },
      },
    ],
    [dropshipData]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns: columnData,
        data: dropshipData,
      },
      useSortBy
    )

  const handleConfirm = async () => {
    try {
      setLoading(true)

      const cleanedOrderData = {
        ...removeTypename(orderData),
        dropships: [], // don't care old dropships data here.
      }

      const { data, errors } = await exportFile({
        variables: {
          dropshipData,
          orderData: cleanedOrderData,
        },
      })

      if (errors && errors.length > 0) {
        console.error("GraphQL Errors:", errors)
        setLoading(false)
        return
      }

      if (!data?.exportFile?.success) {
        console.error("Export failed:", data?.exportFile?.error)
        setLoading(false)
        return
      }

      setLoading(false)
      handleSuccessAction()
    } catch (error) {
      console.error("Error sending data:", error)
    }
  }

  const cancelOnClick = () => {
    setDropshipData([])
    onCancelClick()
  }

  const handleAddSelectedProducts = () => {
    const myProducts = [...dropshipData]
    selectedDropshipProductId.forEach(element => {
      const foundOrder = orderData?.items.find(
        order => order.product_id === element.product_id
      )
      myProducts.push({
        line_id: element.id,
        sku: foundOrder.item.sku,
        name: foundOrder.item.name,
        destination: "customer",
      })
    })
    setDropshipData(myProducts)
    setIsDropshipAddedProduct(true)
    clearSelectedDropshipProductId()
  }

  const removeSingleProduct = row => {
    const lineIdToRemove = row.original.line_id
    setDropshipData(prev =>
      prev.filter(item => item.line_id !== lineIdToRemove)
    )
    clearSelectedDropshipProductId()
  }

  const [modalOpen, setModalOpen] = useState(false)

  const closeSuccessModal = () => {
    window.location.reload()
  }

  const handleSuccessAction = () => {
    setModalOpen(true)
  }

  return (
    <>
      <div
        className="table-responsive react-table mt-1"
        style={{ maxHeight: "650px" }}
      >
        {loading && <PuffLoading />}
        <Modal isOpen={modalOpen} toggle={closeSuccessModal} timeout={300}>
          <ModalHeader toggle={closeSuccessModal}>✅ Success</ModalHeader>
          <ModalBody>
            <Alert color="success">Your action was successful! 🎉</Alert>
          </ModalBody>
          <ModalFooter>
            <Button color="success" onClick={closeSuccessModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
        <Table {...getTableProps()} className="custom-header-css">
          <thead className="table-light table-head">
            {headerGroups.map((headerGroup, i) => (
              <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                <th colSpan="100%">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <h5 style={{ fontSize: "14px", margin: 0 }}>Dropship</h5>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <Label
                        className="form-label mt-2"
                        style={{ fontSize: "14px" }}
                      >
                        Dropshipper
                      </Label>
                      <select
                        className="form-select"
                        style={{ width: "200px" }}
                      >
                        {DROPSHIP_PARTNERS?.map((item, i) => (
                          <option key={i} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="submit"
                        className="ms-2 btn btn-primary waves-effect waves-light btn btn-primary"
                        onClick={() => handleAddSelectedProducts()}
                      >
                        Add Selected Products
                      </button>

                      <button
                        className="ms-2 btn btn-primary waves-effect waves-light btn btn-primary"
                        onClick={() => handleConfirm()}
                        disabled={dropshipData.length === 0}
                      >
                        Confirm
                      </button>

                      <button
                        className="ms-2 btn btn-primary waves-effect waves-light btn btn-primary"
                        style={{
                          background: "#dc3545",
                          borderColor: "#dc3545",
                        }}
                        onClick={() => cancelOnClick()}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </th>
              </tr>
            ))}
          </thead>

          <tbody>
            <tr>
              <td colSpan={3}>
                <Table
                  {...getTableProps()}
                  className="custom-header-css add-move-shipment-table"
                >
                  <thead className="table-light table-head">
                    {headerGroups.map((headerGroup, i) => (
                      <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                        <th></th>
                        {headerGroup.headers.map((column, i) => {
                          return (
                            <th key={i} {...column.getHeaderProps()}>
                              <div>
                                {column.render("Header")?.toUpperCase()}
                              </div>
                            </th>
                          )
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows && rows?.length > 0 ? (
                      rows.map((row, i) => {
                        prepareRow(row)
                        return (
                          <tr key={i} {...row.getRowProps()}>
                            <td>
                              <CancelIcon
                                onClick={() => {
                                  removeSingleProduct(row, i)
                                }}
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                            {row.cells.map((cell, i) => {
                              return (
                                <td
                                  style={{ fontSize: 12 }}
                                  key={i}
                                  {...cell.getCellProps()}
                                >
                                  {cell.render("Cell")}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="no-found-data">
                          No Data Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    </>
  )
}

export default DropshipTable
